import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchTalukas, fetchVillages } from '../APIServices/LocationService/LocationService';
import { fetchDepartments } from '../APIServices/DepartmentsServices';

const LOCAL_KEY = 'gv_locations_v1';
const LocationContext = createContext(null);

export const LocationProvider = ({ children, cacheTTL = 1000 * 60 * 60 }) => {
  const [talukas, setTalukas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [villagesByTaluka, setVillagesByTaluka] = useState({}); // { talukaId: [village...] }
  const [loading, setLoading] = useState(false);

  // load from localStorage if present and not expired
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (raw) {
        const { ts, talukas: t, departments: d, villages } = JSON.parse(raw);
        if (Date.now() - ts < cacheTTL) {
          setTalukas(t || []);
          setDepartments(d || []);
          setVillagesByTaluka(villages || {});
          return;
        }
      }
    } catch (e) { /* ignore */ }

    
    (async () => {
      setLoading(true);
      try {
        const [tData, dData] = await Promise.all([fetchTalukas(), fetchDepartments()]);
        setTalukas(tData || []);
        setDepartments(dData || []);
        const snapshot = { ts: Date.now(), talukas: tData || [], departments: dData || [], villages: {} };
        localStorage.setItem(LOCAL_KEY, JSON.stringify(snapshot));
      } catch (err) {
        console.error('LocationProvider init error', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [cacheTTL]);

  const fetchVillagesForTaluka = useCallback(async (talukaId, force = false) => {
    if (!talukaId) return [];
    if (!force && villagesByTaluka[talukaId]) return villagesByTaluka[talukaId];

    try {
      const vData = await fetchVillages(talukaId);
      setVillagesByTaluka(prev => {
        const next = { ...prev, [talukaId]: vData || [] };
        try {
          const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
          raw.villages = raw.villages || {};
          raw.villages[talukaId] = vData || [];
          localStorage.setItem(LOCAL_KEY, JSON.stringify({ ...raw, ts: raw.ts || Date.now() }));
        } catch (e) {}
        return next;
      });
      return vData || [];
    } catch (err) {
      console.error('fetchVillagesForTaluka error', err);
      return [];
    }
  }, [villagesByTaluka]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tData, dData] = await Promise.all([fetchTalukas(), fetchDepartments()]);
      setTalukas(tData || []);
      setDepartments(dData || []);
      setVillagesByTaluka({}); // clear villages cache (optional)
      localStorage.setItem(LOCAL_KEY, JSON.stringify({ ts: Date.now(), talukas: tData || [], departments: dData || [], villages: {} }));
    } catch (err) {
      console.error('refreshAll error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <LocationContext.Provider
      value={{
        talukas,
        departments,
        villagesByTaluka,
        fetchVillagesForTaluka,
        refreshAll,
        loading,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocations = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocations must be used inside LocationProvider');
  return ctx;
};